import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEnterprise } from '../../../contexts/EnterpriseContext';
import './index.css';
import './index.mobile.css';

function SectionTitle({ children }) {
  return (
    <div className="ffov-section-title">
      <span className="ffov-section-title-text">{children}</span>
      <span className="ffov-section-dot" />
    </div>
  );
}

export default function Overview() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { enterprise, surveyProgress, validationStatus, verifyRound, verifyPaper } = useEnterprise() || {};

  const vs = validationStatus ?? 0;
  const vr = verifyRound ?? 0;
  const stage = (() => {
    if (vs === 50 || vs === 60) return 6;
    if (vs === 20 && vr >= 2) return 5;
    if (vs === 10 && vr >= 2) return 4;
    if (vs === 20) return 3;
    if (vs === 10) return 2;
    if (surveyProgress === 2) return 1;
    return 0;
  })();

  const reviewStepStatus =
    ['locked', 'preparing', 'active', 'reviewing', 'active', 'reviewing', 'done'][stage] ??
    'locked';

  const STEPS = [
    { key: 'registration', label: t('ff.overview.step.registration'), status: 'done' },
    {
      key: 'materials',
      label: t('ff.overview.step.materials'),
      status: stage > 0 || surveyProgress === 2 ? 'done' : 'active',
    },
    { key: 'review', label: t('ff.overview.step.review'), status: reviewStepStatus },
    {
      key: 'publish',
      label: t('ff.overview.step.publish'),
      status: stage === 6 ? 'pending_publish' : 'locked',
    },
  ];

  const infoRows = enterprise
    ? [
        { label: t('ff.overview.info.companyName'), value: enterprise.companyName },
        { label: t('ff.overview.info.account'), value: enterprise.username, isCode: true },
        { label: t('ff.overview.info.email'), value: enterprise.email },
        { label: t('ff.overview.info.contact'), value: enterprise.nickname || enterprise.username },
      ]
    : [];

  const toUiStatus = (status) => {
    if (status === 'done' || status === 'locked') return status;
    return 'active';
  };

  const statusText = (status) => {
    if (status === 'done') return t('ff.overview.status.done');
    if (status === 'active') return t('ff.overview.status.active');
    if (status === 'preparing') return t('ff.overview.status.preparing');
    if (status === 'reviewing') return t('ff.overview.status.reviewing');
    if (status === 'pending_publish') return t('ff.overview.status.pending_publish');
    return t('ff.overview.status.locked');
  };

  const noticeDeadline = (() => {
    const hardDeadline = new Date('2026-10-20');
    if (!enterprise?.activatedAt) return '10/20';
    const activated = new Date(enterprise.activatedAt);
    const computed = new Date(activated);
    computed.setDate(computed.getDate() + 21);
    const deadline = computed > hardDeadline ? hardDeadline : computed;
    const now = new Date();
    if (now > hardDeadline) return '10/20';
    return `${deadline.getMonth() + 1}/${deadline.getDate()}`;
  })();

  const subtitle = (() => {
    if (stage >= 1 && stage <= 6) return t(`ff.overview.subtitle.stage${stage}`);
    if (surveyProgress === 2) return t('ff.overview.subtitle');
    return t('ff.overview.subtitle.materials');
  })();

  const notice = (() => {
    if (stage === 1) {
      const isEarly = new Date() < new Date('2026-08-25');
      return t(isEarly ? 'ff.overview.notice.stage1.early' : 'ff.overview.notice.stage1.late');
    }
    if (stage >= 2 && stage <= 6) return t(`ff.overview.notice.stage${stage}`, { count: verifyPaper?.items?.length ?? 0 });
    if (surveyProgress === 2) return t('ff.overview.notice.submitted');
    return t('ff.overview.notice', { deadline: noticeDeadline });
  })();

  return (
    <div className="ffov-page">
      <h1 className="ffov-welcome">
        {t('ff.overview.welcome').replace('{name}', enterprise?.companyName || '')}
      </h1>
      <p className="ffov-subtitle">{subtitle}</p>

      {/* 评选进度 */}
      <div className="ffov-card">
        <SectionTitle>{t('ff.overview.section.progress')}</SectionTitle>
        <div className="ffov-steps">
          {STEPS.map((step, i) => (
            <React.Fragment key={step.key}>
              <div className={`ffov-step ffov-step--${toUiStatus(step.status)}`}>
                <div className="ffov-step-pill-wrapper">
                  <div className="ffov-step-pill">
                    <span className="ffov-step-label">{step.label}</span>
                  </div>
                </div>
                <span className="ffov-step-status-text">{statusText(step.status)}</span>
              </div>
              {i < STEPS.length - 1 && (
                <div
                  className={`ffov-step-connector ${STEPS[i + 1].status !== 'locked' && step.status === 'done' ? 'ffov-step-connector--active' : ''}`}
                />
              )}
            </React.Fragment>
          ))}
        </div>
        <div className="ffov-notice-bar">
          <span className="ffov-notice-text">{notice}</span>
          {(stage === 2 || stage === 4) && (
            <button
              className="ffov-notice-btn"
              onClick={() => navigate('/family-friendly/questionnaire')}
            >
              {t('ff.overview.btn.view_submit')}
            </button>
          )}
          {stage === 0 && surveyProgress !== 2 && (
            <button
              className="ffov-notice-btn"
              onClick={() => navigate('/family-friendly/questionnaire')}
            >
              {t('ff.overview.btn.survey')}
            </button>
          )}
        </div>
      </div>

      {/* 企业报名信息 */}
      <div className="ffov-card">
        <div className="ffov-card-header">
          <SectionTitle>{t('ff.overview.section.companyInfo')}</SectionTitle>
          <button
            className="ffov-edit-btn"
            onClick={() => navigate('/family-friendly/company-info')}
          >
            {t('ff.overview.btn.edit')}
          </button>
        </div>
        <div className="ffov-info-table">
          {infoRows.map((row) => (
            <div key={row.label} className="ffov-info-row">
              <span className="ffov-info-label">{row.label}</span>
              {row.isCode ? (
                <span className="ffov-info-code">{row.value}</span>
              ) : (
                <span className="ffov-info-value">{row.value}</span>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
